import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { FirebaseStorageService } from '@shared/services/firebase-storage.service';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { AttendanceJustificationTypeRecord } from '../attendance-justification-type/attendance-justification-type.interface';
import {
  AttendanceJustificationAttachment,
  AttendanceJustificationPayload,
  AttendanceJustificationRecord,
} from '../attendance-justification/attendance-justification.interface';
import { AttendanceResultRecord } from '../attendance-result/attendance-result.interface';

export interface AbsenceJustificationDialogData {
  result: AttendanceResultRecord;
  flow?: 'absence' | 'delay' | 'early_exit';
  minutes?: number | null;
}

@Component({
  selector: 'app-absence-justification-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './absence-justification-dialog.component.html',
  styleUrl: './absence-justification-dialog.component.scss',
})
export class AbsenceJustificationDialogComponent implements OnInit {
  justificationTypes: AttendanceJustificationTypeRecord[] = [];
  selectedTypeId = '';
  comment = '';
  selectedFiles: File[] = [];
  isLoading = true;
  isSaving = false;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly firebaseStorageService = inject(FirebaseStorageService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  constructor(
    private readonly dialogRef: MatDialogRef<AbsenceJustificationDialogComponent, AttendanceJustificationRecord | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AbsenceJustificationDialogData
  ) {}

  ngOnInit(): void {
    void this.loadJustificationTypes();
  }

  get selectedType(): AttendanceJustificationTypeRecord | null {
    return this.justificationTypes.find((type) => type.attendanceJustificationTypeId === this.selectedTypeId) || null;
  }

  get employeeName(): string {
    return this.data.result.employeeName || this.data.result.employeeNumber || this.data.result.employeeId;
  }

  get employeeNumber(): string {
    return this.data.result.employeeNumber || '—';
  }

  get formattedWorkDate(): string {
    const [year, month, day] = String(this.data.result.workDate || '').split('-').map(Number);
    if (!year || !month || !day) return this.data.result.workDate || '—';
    return new Intl.DateTimeFormat(this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(new Date(year, month - 1, day));
  }

  get translationPrefix(): string {
    if (this.data.flow === 'delay') return 'delaysCatalog.quickJustification';
    if (this.data.flow === 'early_exit') return 'earlyExitsCatalog.quickJustification';
    return 'absencesCatalog.quickJustification';
  }

  get isDelayFlow(): boolean {
    return this.data.flow === 'delay';
  }

  get isEarlyExitFlow(): boolean {
    return this.data.flow === 'early_exit';
  }

  get isPartialMinutesFlow(): boolean {
    return this.isDelayFlow || this.isEarlyExitFlow;
  }

  get attendanceEffect(): 'justify_day' | 'justify_delay' | 'justify_early_exit' {
    if (this.isDelayFlow) return 'justify_delay';
    if (this.isEarlyExitFlow) return 'justify_early_exit';
    return 'justify_day';
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []).filter((file) => (file.type || '').startsWith('image/'));
    this.selectedFiles = [...this.selectedFiles, ...files].filter(
      (file, index, all) => all.findIndex((item) => item.name === file.name && item.size === file.size) === index
    );
    if (input) input.value = '';
  }

  removeFile(index: number): void {
    this.selectedFiles = this.selectedFiles.filter((_, currentIndex) => currentIndex !== index);
  }

  close(): void {
    if (!this.isSaving) this.dialogRef.close(null);
  }

  async save(): Promise<void> {
    const type = this.selectedType;
    if (!type) {
      this.notify('snackbar-warning', `${this.translationPrefix}.validationType`);
      return;
    }
    if (type.requireComment && !this.comment.trim()) {
      this.notify('snackbar-warning', 'attendanceJustificationsCatalog.notifications.validationCommentRequired');
      return;
    }
    if (type.requireAttachment && !this.selectedFiles.length) {
      this.notify('snackbar-warning', 'attendanceJustificationsCatalog.notifications.validationAttachmentRequired');
      return;
    }

    this.isSaving = true;
    try {
      const attachments = await this.uploadAttachments();
      const payload: AttendanceJustificationPayload = {
        employeeId: this.data.result.employeeId,
        attendanceJustificationTypeId: type.attendanceJustificationTypeId,
        scope: this.isPartialMinutesFlow ? 'partial_minutes' : 'full_day',
        workDate: this.data.result.workDate,
        comment: this.comment.trim() || null,
        attachments,
      };
      if (this.isPartialMinutesFlow) {
        payload.minutes = Math.max(1, Number(this.data.minutes ?? this.data.result.delayMinutes ?? 0));
      }
      const created = await firstValueFrom(
        this.apiService.post<AttendanceJustificationRecord>('attendance-justifications', payload)
      );
      this.dialogRef.close(created);
    } catch (error) {
      console.error('Error creating quick attendance justification', error);
      this.snackBar.open(
        extractApiErrorMessage(error, this.translate.instant('attendanceJustificationsCatalog.notifications.saveError')),
        undefined,
        { duration: 4500, panelClass: ['snackbar-danger'], horizontalPosition: 'center', verticalPosition: 'bottom' }
      );
    } finally {
      this.isSaving = false;
    }
  }

  private async loadJustificationTypes(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceJustificationTypeRecord[]>('attendance-justification-types', {
          params: { attendanceEffect: this.attendanceEffect },
        })
      );
      this.justificationTypes = (response || [])
        .filter((type) => type.isActive !== false)
        .filter((type) => type.allowAdminCreate !== false)
        .filter((type) => type.attendanceEffect === this.attendanceEffect)
        .sort((left, right) => left.label.localeCompare(right.label));
      if (this.justificationTypes.length === 1) {
        this.selectedTypeId = this.justificationTypes[0].attendanceJustificationTypeId;
      }
    } catch (error) {
      console.error('Error loading quick justification types', error);
      this.notify('snackbar-danger', `${this.translationPrefix}.loadTypesError`);
    } finally {
      this.isLoading = false;
    }
  }

  private async uploadAttachments(): Promise<AttendanceJustificationAttachment[]> {
    return await Promise.all(this.selectedFiles.map(async (file) => {
      const uploaded = await this.firebaseStorageService.uploadAttendanceJustificationAttachment(
        file,
        this.data.result.employeeId
      );
      return {
        url: uploaded.url,
        storagePath: uploaded.storagePath,
        name: file.name,
        contentType: file.type || null,
        size: file.size,
        uploadedAt: new Date().toISOString(),
      } satisfies AttendanceJustificationAttachment;
    }));
  }

  private notify(panelClass: string, translationKey: string): void {
    this.snackBar.open(this.translate.instant(translationKey), undefined, {
      duration: 4000,
      panelClass: [panelClass],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }
}
